// import { ExternalObject } from "../../../../api/integrations2.0";

export interface OAuthConfig {
  // Standard OAuth fields
  clientId: string;
  clientSecret: string;
  authorizationUrl?: string;
  tokenUrl?: string;
  scopes?: string[];
  additionalParams?: Record<string, string>;

  // Subdomain handling
  requiresSubdomain?: boolean;
  subdomainPlaceholder?: string;
  subdomainValidation?: {
    validator: 'validateSalesforceURL';
    errorMessage: string;
  };
  authorizationUrlTemplate?: string;
  uriPostfix?: string;
  testDomain?: string;

  // State and metadata
  metadata?: Record<string, unknown>;

  // Alternative auth methods (e.g., for Jira)
  alternateAuthMethods?: {
    apiKey?: {
      required: boolean;
      additionalFields?: {
        organizationId?: boolean;
        onPremiseUrl?: boolean;
      };
    };
  };
}

export interface AlternateAuthField {
  key: string;
  type: 'text' | 'select' | 'toggle' | 'multiselect';
  label: string;
  required: boolean;
  placeholder?: string;
  description?: string;
  default?: any;
  validation?: {
    pattern?: string;
    min?: number;
    max?: number;
  };
}

export interface ObjectMappingField extends SettingsField {
  type: "object_mapping";
  availableObjects: Array<{
    value: string;
    label: string;
    description?: string;
    fields?: Array<{
      value: string;
      label: string;
      type: string;
    }>;
  }>;
  systemEntities: Array<{
    value: string;
    label: string;
    description?: string;
  }>;
}

export interface SettingsField {
  key: string;
  type: "text" | "select" | "toggle" | "multiselect" | "webhook" | "object_mapping";
  label: string;
  required: boolean;
  default?: any;
  options?: Array<{
    value: string;
    label: string;
  }>;
  validation?: {
    pattern?: string;
    min?: number;
    max?: number;
  };
}

export interface SettingsSection {
  key: string;
  title: string;
  description?: string;
  fields: SettingsField[];
}

export interface SettingsSchema {
  availableFilters: Array<{
    key: string;
    label: string;
    type: string;
    required: boolean;
    default?: any;
    options?: Array<{ value: string; label: string }>;
  }>;
  sections: SettingsSection[];
}


export enum AuthType {
  OAUTH2 = 'oauth2',
  API_KEY = 'api-key'
}

export type ApiKeyFieldType = 'password' | 'text' | 'url' | 'email';

export interface ApiKeyFieldValidation {
  pattern?: string;
  message?: string;
  minLength?: number;
  maxLength?: number;
}

export interface ApiKeyField {
  key: string;
  label: string;
  description: string;
  placeholder: string;
  type: ApiKeyFieldType;
  required: boolean;
  validate?: ApiKeyFieldValidation;
}

export interface ApiKeyConfig {
  name: string;
  description: string;
  requiresSubdomain: boolean;
  fields: ApiKeyField[];
}

export interface AuthConfiguration {
  name?: string;
  description?: string;
  placeholderText?: string;
  
  requiresSubdomain?: boolean;
  subdomainFormat?: string;//TODO: is this Needed?
}

export interface OAuthConfiguration extends AuthConfiguration {
  authorizationUrl: string; // URL with {subdomain} placeholder if requiresSubdomain is true
  tokenUrl: string; // URL with {subdomain} placeholder if requiresSubdomain is true
  clientId: string;
  clientSecret: string;
  scopes: string[];
  additionalParams?: Record<string, string>;
  testDomain?: string;
  metadata?: Record<string, unknown>;
}

export interface ApiKeyConfiguration extends AuthConfiguration {
  name?: string;
  description?: string;
  fields?: ApiKeyField[];
}

export interface ConnectionOptions {
  supportedAuthTypes: AuthType[];
  oauth?: OAuthConfiguration;
  apiKey?: ApiKeyConfiguration;
}

export interface WebhookConfig {
  enabled: boolean;
  events: string[];
  urlTemplate: string;
  headers?: Record<string, string>;
  payloadTemplate?: string;
}

export interface SyncObjectConfig {
  name: string;
  bagelObjectKey: string; // The corresponding object key in Bagel (e.g., 'contacts', 'deals') defult;
  isDefault?: boolean;
  limit?: number;
  sort?: { field: string; order: 'asc' | 'desc' }[];
  startDate?: Date;
  endDate?: Date;
  dateField?: 'createdAt' | 'updatedAt';
  includeCommentEvents?: boolean;
  options?: { 
    batchSize: number;
    [key: string]: any;
  };
  mappings?: Array<{
    bagelField: string;
    externalField: string;
    label?: string;
    type?: string;
  }>;
}


export interface AvailableIntegration extends ConnectionOptions {
  _id: any;
  key: string;
  name: string;
  description: string;
  icon: string;
  status: 'active' | 'beta' | 'deprecated';
  settings?: SettingsSchema;
  webhook?: WebhookConfig;
  objects?: SyncObjectConfig[];
}
//TODO: make sure this is synced with backend Integration type (commons soon)
export interface ConnectedIntegration {
  status: 'active'|'setup'|'errored'|'disabled';
  name: string;
  description: string;
  key: string;
  settings?: {
    syncFilters?: Record<string, any>;
  };
  tokenObject?: {
    access_token?: string;
    token_type?: string;
    type?: string;
    organizationId?: string;
    apiUrl?: string;
    [key: string]: string | undefined;
  };
}

export interface Integration {
  available: AvailableIntegration;
  connected?: ConnectedIntegration;
}

export interface IntegrationError {
  code: string;
  message: string;
  timestamp: Date;
} 